import { useState, useEffect } from 'react';

export function useMobile() {
  const [mobile, setMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 1023);
  useEffect(() => {
    // FIX 4: was only 'resize' — iOS fires orientationchange before resize (or not at all)
    // F1: raised breakpoint 768 → 1023 so narrow laptops/tablets get hamburger instead of clipped header.
    // NOTE: JSDOM test environment uses innerWidth=1024 by default — breakpoint <=1023 keeps 1024 as
    // desktop-mode in tests (backward-compat). Real 1024px viewport gets compact-desktop layout (~974px content fits).
    const check = () => setMobile(window.innerWidth <= 1023);
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', () => setTimeout(check, 100)); // 100ms delay for iOS layout settle
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);
  return mobile;
}

/* useMediaQuery — live boolean for an arbitrary media query. Added 2026-08-04 for the
   Payments / Customer Payments mobile modal, which needs its JS behaviour (the collapsed
   Open By picker) to flip at EXACTLY the width its stylesheet flips at. useMobile above
   answers a different question (<=1023 → hamburger nav), so reusing it would collapse the
   name list on narrow laptops too.

   jsdom has no window.matchMedia: the guard makes the hook return false there, i.e. every
   existing test keeps rendering the desktop branch. */
export function useMediaQuery(query) {
  const supported = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const [matches, setMatches] = useState(() => (supported() ? window.matchMedia(query).matches : false));
  useEffect(() => {
    if (!supported()) return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();  // width may have changed between the initializer and this effect
    // addListener is the pre-Safari-14 spelling — still the only one on older iOS
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);
  return matches;
}

/* useViewportWidth — returns current innerWidth, updates on resize + orientationchange.
   SSR-safe: returns 1440 (full-desktop assumption) on server.
   Used by BlitzHeader to select layout tier without CSS media queries. */
export function useViewportWidth() {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1440);
  useEffect(() => {
    const check = () => setWidth(window.innerWidth);
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', () => setTimeout(check, 100));
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);
  return width;
}
